// Helpers for reasoning about a policy's maker-checker sequence stage.
//
// A policy carries a maker_checks array: [{ guid, type, label, sequence,
// status }]. The only actionable stage is the lowest-sequence check that is
// still Pending (the server owns sequencing and separation of duties). These
// helpers mirror the current-stage logic in the MakerChecks component so the
// New Business and Sales Manager dashboards can partition policies by stage
// client-side.

// Return the sorted maker_checks (ascending sequence), or an empty array.
function sortedChecks(policy) {
  if (!policy || !Array.isArray(policy.maker_checks)) {
    return [];
  }
  return policy.maker_checks.slice().sort(function(a, b) {
    return (a.sequence || 0) - (b.sequence || 0);
  });
}

// The single actionable stage: the lowest-sequence check still Pending, else
// null (no maker_checks, or every stage already decided).
export function getCurrentPendingCheck(policy) {
  var checks = sortedChecks(policy);
  for (var i = 0; i < checks.length; i++) {
    if (checks[i].status === "Pending") {
      return checks[i];
    }
  }
  return null;
}

// True when the policy is awaiting its FIRST maker-checker step, i.e. the
// lowest-sequence check is still Pending. Policies with no maker_checks return
// false so legacy/non-sequenced policies stay out of the Sales Manager queue.
export function isFirstStepPending(policy) {
  var checks = sortedChecks(policy);
  if (!checks.length) {
    return false;
  }
  return checks[0].status === "Pending";
}

// True when every check ordered before `current` has been Approved. A stage
// basket must never hold a policy whose earlier stage was rejected (or is
// otherwise undecided) — only a clean run of approvals up to here qualifies.
function precedingChecksApproved(policy, current) {
  var checks = sortedChecks(policy);
  for (var i = 0; i < checks.length; i++) {
    if (checks[i] === current) {
      return true;
    }
    if (String(checks[i].status || "").toLowerCase() !== "approved") {
      return false;
    }
  }
  return false;
}

// True when the policy is sitting on exactly the stage described by `stage`
// ({ label, sequenceIndex }) AND every stage before it is Approved.
//
// This is what keeps a level-2 basket clean: a policy whose level-1 check is
// still Pending has level-1 as its current check, so it never matches level 2;
// and a policy whose level-1 check was Rejected fails the preceding-approvals
// guard. Matching is on the configured label (what reviewers see everywhere
// else in the UI), falling back to position in the sequence when a check
// carries no label.
export function isPendingStage(policy, stage) {
  if (!stage) {
    return false;
  }
  var current = getCurrentPendingCheck(policy);
  if (!current || !precedingChecksApproved(policy, current)) {
    return false;
  }
  if (stage.label && current.label) {
    return (
      String(current.label).trim().toLowerCase() ===
      String(stage.label).trim().toLowerCase()
    );
  }
  return sortedChecks(policy).indexOf(current) === stage.sequenceIndex;
}

// True when the policy is still somewhere inside its approval sequence, i.e.
// some check is Pending. Used by baskets that are keyed on something other
// than the stage itself (the Need Action tab) but must still exclude policies
// that have left the sequence altogether.
export function hasPendingApprovalStage(policy) {
  return !!getCurrentPendingCheck(policy);
}

// Human-readable name of the current pending stage (e.g. "Sales Approval").
export function stageLabel(policy) {
  var currentCheck = getCurrentPendingCheck(policy);
  if (!currentCheck) {
    return "N/A";
  }
  return currentCheck.label || currentCheck.type || "N/A";
}

// True when the policy's current pending stage is an AML stage — either the
// "AML Check"/"AML approval" AgentApproval (matched by label) or the
// system-flagged AmlApproval (matched by type). These are handled in the
// dedicated AML queue, so they should not surface on New Business Un Confirmed.
export function isAmlStagePending(policy) {
  var currentCheck = getCurrentPendingCheck(policy);
  if (!currentCheck) {
    return false;
  }
  var label = String(currentCheck.label || "").toLowerCase();
  var type = String(currentCheck.type || "").toLowerCase();
  return label.indexOf("aml") !== -1 || type.indexOf("aml") !== -1;
}

// The screening stages that own their own dashboards: AML, PEP and IPRS. A
// policy sitting on one of these belongs in that dedicated queue, not in a
// general basket.
var SCREENING_STAGES = ["aml", "pep", "iprs"];

export function isScreeningStagePending(policy) {
  var currentCheck = getCurrentPendingCheck(policy);
  if (!currentCheck) {
    return false;
  }
  var label = String(currentCheck.label || "").toLowerCase();
  var type = String(currentCheck.type || "").toLowerCase();
  for (var i = 0; i < SCREENING_STAGES.length; i += 1) {
    var stage = SCREENING_STAGES[i];
    if (label.indexOf(stage) !== -1 || type.indexOf(stage) !== -1) {
      return true;
    }
  }
  return false;
}

// The Need Action basket: still inside the approval sequence, but NOT parked on
// a screening stage.
//
// hasPendingApprovalStage alone was too loose. A policy flagged need_action
// while sitting on an AML, PEP or IPRS check would surface in the broker's Need
// Action tab as well as in the screening queue that owns it, so two teams saw
// the same policy as theirs to act on. Every other basket here excludes the
// screening stages; this one now matches.
export function isPendingNonScreeningStage(policy) {
  return hasPendingApprovalStage(policy) && !isScreeningStagePending(policy);
}

// This policy's own copy of the check a stage descriptor ({ label,
// sequenceIndex }) names, or null. Same matching rule as isPendingStage: on
// the configured label, falling back to position in the sequence for checks
// saved without one.
export function getStageCheck(policy, stage) {
  if (!stage) {
    return null;
  }
  var checks = sortedChecks(policy);
  if (stage.label) {
    for (var i = 0; i < checks.length; i++) {
      var label = checks[i].label;
      if (
        label &&
        String(label).trim().toLowerCase() ===
          String(stage.label).trim().toLowerCase()
      ) {
        return checks[i];
      }
    }
  }
  return checks[stage.sequenceIndex] || null;
}

// True when this policy's copy of `stage` was decided the given way
// ("Approved" / "Rejected"). Unlike isPendingStage there is no
// preceding-approvals guard: a decision already taken at this stage belongs in
// this stage's log whatever happened before or after it.
export function isStageDecision(policy, stage, decision) {
  var check = getStageCheck(policy, stage);
  if (!check) {
    return false;
  }
  return (
    String(check.status || "").toLowerCase() ===
    String(decision || "").trim().toLowerCase()
  );
}

// The stage a policy was corrected for: returning a policy for approval stamps
// additional_details.corrected_for_stage with the stage it was sitting on, so
// the correction goes back to whoever asked for it rather than to the front of
// the queue. Once that stage decides, the policy moves on and the stamp stops
// matching, so nothing has to clear it.
// The additional_details fragment that records where a policy is in that round
// trip. Returning it stamps the stage it is sitting on — the one that asked
// for the changes — so the correction lands in that stage's Corrected tab.
// Sending it out for changes clears the stamp, so a policy is never listed as
// both out for correction and corrected.
export function correctedForStageUpdate(policy, needAction) {
  if (needAction) {
    return { corrected_for_stage: null };
  }
  var current = getCurrentPendingCheck(policy);
  return { corrected_for_stage: (current && current.label) || null };
}

export function isCorrectedForStage(policy, stage) {
  if (!stage || !stage.label) {
    return false;
  }
  var details = (policy && policy.additional_details) || {};
  return (
    String(details.corrected_for_stage || "")
      .trim()
      .toLowerCase() === String(stage.label).trim().toLowerCase()
  );
}
